import React, { memo, useCallback, useMemo, useState } from "react";
import { useTheme } from "@emotion/react";
import { FormattedMessage } from "react-intl";
import styled from "@emotion/styled";
import useStore from "store";

import Button from "components/Button";
import Text from "components/Text";

import messages from "store/messages";

function GearboxType() {
  const { other, palette } = useTheme();
  const { filters, setFilters } = useStore();
  const { gearboxFilter } = filters;
  const [typesSelected, setTypesSelected] = useState<string[]>(gearboxFilter || []);

  const Body = useMemo(() => styled.div`
    position: relative;
    padding-bottom: 15px;
    &:hover #gearbox-types {
      visibility: visible;
      opacity: 1;
      transform: translateY(-25px);
    }
  `, []);

  const Preview = useMemo(() => styled.div`
    display: flex;
    height: 36px;
    align-items: center;
    border-radius: ${other.borderRadius};
    border: 1px solid ${palette.primary.dark};
    padding: 5px 15px;
    cursor: pointer;
    position: relative;
    &.active {
      border: 1px solid ${palette.additional.dark};
      background: ${palette.additional.light};
    }
    & #close-icon{
      margin-left: 10px;
    }
  `, [other.borderRadius, palette.additional.dark, palette.additional.light, palette.primary.dark]);
  
  const RangeModal = useMemo(() => styled.div`
    position: absolute;
    top: calc(100% + 20px);
    border-radius: ${other.borderRadius};
    border: 1px solid ${palette.secondary.main};
    background: white;
    z-index: 100;
    overflow: hidden;
    opacity: 0;
    width: 350px;
    visibility: hidden;
    transition: 0.2s ease all;
    box-shadow: 4px 8px 10px 0px rgba(34, 60, 80, 0.2);
    @media (min-width:768px){
      left: 0;
    }
    @media (max-width:768px){
      right: 0;
    }
    &.no-hover{
      opacity: 0;
      pointer-events: none;
    }
  `, [other.borderRadius, palette.secondary.main]);

  const Container = useMemo(
    () => styled.div`
    display: flex;
    flex-direction: column;
    padding: 20px;
    gap: 20px;
  `, []);
  
  const Option = useMemo(
    () => styled.div`
      display: flex;
      align-items: center;
      gap: 20px;
    & > input {
      height: 30px;
      min-width: 30px;
      border-radius: ${other.borderRadius};
    }
    & > label {
      cursor: pointer;
      width: 100%;
    }
  `, [other.borderRadius]);

  // Buttons
  const Buttons = styled.div`
    background: ${palette.secondary.light};
    display: flex;
    justify-content: space-between;
    padding: 15px 20px;
    & > button {
      width: 30%;
    }
  `;

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const { id, checked } = e.target;
    if (checked) {
      setTypesSelected((prev) => prev.concat(id.toLowerCase()));
    } else {
      setTypesSelected((prev) => prev.filter((item) => item !== id.toLowerCase()));
    }
  }, []);

  const handleHover = useCallback((type:"add" | "delete" = "delete") => {
    const element = document.getElementById("gearbox-types");
    if (type === "add") { 
      element?.classList.add("no-hover"); 
    } else {
      element?.classList.remove("no-hover"); 
    }
  }, []);

  const onSelect = useCallback(() => {
    setFilters({ ...filters, gearboxFilter: typesSelected });
    handleHover("add");
  }, [filters, handleHover, setFilters, typesSelected]);

  const onClear = useCallback(() => {
    setTypesSelected([]);
    setFilters({ ...filters, gearboxFilter: [] });
    handleHover("add");
  }, [filters, handleHover, setFilters]);

  const isCarTypesExist = gearboxFilter && gearboxFilter.length > 0;

  const gearboxType = useMemo(() => [
    { value: "Automatic", label: <FormattedMessage {...messages.Automatic} /> },
    { value: "Manual", label: <FormattedMessage {...messages.Manual} /> },
  ], []);

  return (
    <Body onMouseEnter={() => handleHover()}>
      <Preview className={isCarTypesExist ? "active" : ""}>
        <Text
          tag="span"
          className="price-span"
          variant={isCarTypesExist ? "normalSmallAdditional" : "normalSmall"}
        >
          <FormattedMessage {...messages.gearboxTypes} />
        </Text>
        {isCarTypesExist && (
          <Text tag="span" variant="normalAdditional" id="close-icon" onClick={onClear}>
            <i className="las la-times-circle" />
          </Text>
        )}
      </Preview>
      <RangeModal id="gearbox-types">
        <Container>
          {gearboxType.map(({ value, label }) => (
            <Option key={value}>
              <input
                type="checkbox"
                id={value}
                checked={typesSelected.includes(value.toLowerCase())}
                onChange={onChange}
              />
              <label htmlFor={value}>
                <Text
                  tag="span"
                  variant="normal"
                >
                  {label}
                </Text>
              </label>
            </Option>
          ))}
        </Container>
        <Buttons>
          <Button
            onClick={onClear}
            background="transparent"
            textVariant="normalAdditional"
            id="clear-gearbox-button"
          >
            <FormattedMessage {...messages.clear} />
          </Button>
          <Button
            onClick={onSelect}
            textVariant="normalSecondary"
            id="apply-gearbox-button"
          >
            <FormattedMessage {...messages.apply} />
          </Button>
        </Buttons>
      </RangeModal>
    </Body>
  );
}
export default memo(GearboxType);
